import {
  arrow,
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
} from "@floating-ui/dom";

export const Tooltip = {
  mounted() {
    this.trigger = this.el.parentElement;
    this.originalTooltip = this.el;

    // Set aria-describedby on the trigger for screen reader accessibility
    this.trigger.setAttribute("aria-describedby", this.originalTooltip.id);

    // Hide the original tooltip element completely
    this.originalTooltip.style.display = "none";

    this.portalTooltip = null;
    this.cleanup = null;

    // Detect if trigger lives inside a fixed-position ancestor
    this.isFixed = (() => {
      let node = this.trigger;
      while (node && node !== document.body) {
        if (getComputedStyle(node).position === "fixed") return true;
        node = node.parentElement;
      }
      return false;
    })();

    this.show = () => {
      if (this.portalTooltip) return;

      // Create a clone to put in the body
      this.portalTooltip = this.originalTooltip.cloneNode(true);
      // Remove ID to avoid duplicates
      this.portalTooltip.removeAttribute("id");
      // Remove hook attribute just in case
      this.portalTooltip.removeAttribute("phx-hook");

      // Style the portal tooltip
      Object.assign(this.portalTooltip.style, {
        position: this.isFixed ? "fixed" : "absolute",
        top: "0",
        left: "0",
        width: "max-content",
        maxWidth: "13rem",
        zIndex: "99999", // Super high z-index
        opacity: "0",
        transform: "scale(0.9)",
        transition: window.matchMedia("(prefers-reduced-motion: reduce)")
          .matches
          ? "none"
          : "opacity 0.1s ease-in-out, transform 0.1s ease-in-out",
      });

      // Remove the inline display: none inherited from the original
      this.portalTooltip.style.removeProperty("display");
      // Allow pointer events on portal so click-away detection works correctly
      if (this.isClickable) {
        this.portalTooltip.classList.remove("pointer-events-none");
      }
      this.portalTooltip.classList.add("floating-ui-tooltip");

      document.body.appendChild(this.portalTooltip);

      // Trigger reflow to enable transition
      requestAnimationFrame(() => {
        if (!this.portalTooltip) return;
        Object.assign(this.portalTooltip.style, {
          opacity: "1",
          transform: "scale(1)",
        });
      });

      const arrowElement = this.portalTooltip.querySelector("[data-arrow]");
      if (!arrowElement) return;
      const placement = this.originalTooltip.dataset.placement || "top";

      this.cleanup = autoUpdate(
        this.trigger,
        this.portalTooltip,
        () => {
          computePosition(this.trigger, this.portalTooltip, {
            placement,
            strategy: this.isFixed ? "fixed" : "absolute",
            middleware: [
              offset(8),
              flip(),
              shift({ padding: 5 }),
              arrow({ element: arrowElement }),
            ],
          }).then(({ x, y, placement, middlewareData }) => {
            if (!this.portalTooltip) return;
            Object.assign(this.portalTooltip.style, {
              left: `${x}px`,
              top: `${y}px`,
            });

            if (middlewareData.arrow) {
              const { x: arrowX, y: arrowY } = middlewareData.arrow;

              const staticSide = {
                top: "bottom",
                right: "left",
                bottom: "top",
                left: "right",
              }[placement.split("-")[0]];

              Object.assign(arrowElement.style, {
                left: arrowX != null ? `${arrowX}px` : "",
                top: arrowY != null ? `${arrowY}px` : "",
                right: "",
                bottom: "",
                [staticSide]: "-4px",
              });
            }
          });
        },
        { ancestorScroll: false },
      );
    };

    this.hide = () => {
      if (this.cleanup) {
        this.cleanup();
        this.cleanup = null;
      }
      if (this.portalTooltip) {
        this.portalTooltip.remove();
        this.portalTooltip = null;
      }
    };

    this.dismissOnClickAway = this.el.dataset.dismissOnClickAway === "true";

    if (!this.dismissOnClickAway) {
      this.trigger.addEventListener("mouseenter", this.show);
      this.trigger.addEventListener("mouseleave", this.hide);
      this.trigger.addEventListener("focus", this.show);
      this.trigger.addEventListener("blur", this.hide);
    }

    this.isClickable = this.el.dataset.clickable === "true";

    if (this.isClickable) {
      // Toggle tooltip on click/tap (for mobile support)
      this.handleTriggerClick = (e) => {
        if (this.portalTooltip) {
          this.hide();
        } else {
          // Hide any other open tooltips first
          document
            .querySelectorAll(".floating-ui-tooltip")
            .forEach((el) => el.remove());
          this.show();
        }
      };
      this.trigger.addEventListener("click", this.handleTriggerClick);

      // Dismiss tooltip when clicking/tapping outside
      this._clickAwayHandler = (e) => {
        if (
          this.portalTooltip &&
          !this.trigger.contains(e.target) &&
          !this.portalTooltip.contains(e.target)
        ) {
          this.hide();
        }
      };
      document.addEventListener("click", this._clickAwayHandler);
    } else {
      // Non-clickable: hide stray tooltips on click
      this.handleTriggerClick = () => {
        document
          .querySelectorAll(".floating-ui-tooltip")
          .forEach((el) => el.remove());
      };
      this.trigger.addEventListener("click", this.handleTriggerClick);
    }

    if (this.dismissOnClickAway) {
      this.show();
      this._documentClickHandler = (e) => {
        if (!this.trigger.contains(e.target)) {
          this.hide();
          document.removeEventListener("click", this._documentClickHandler);
        }
      };
      setTimeout(
        () => document.addEventListener("click", this._documentClickHandler),
        100,
      );
    } else if (this.el.dataset.forceVisible === "true") {
      this.show();
    }
  },

  updated() {
    if (this.portalTooltip) {
      this.portalTooltip.innerHTML = this.originalTooltip.innerHTML;
    }

    if (this.el.dataset.forceVisible === "true") {
      this.show();
    } else if (
      this.el.dataset.forceVisible === "false" &&
      !this.trigger.matches(":hover") &&
      !this.trigger.matches(":focus")
    ) {
      // Hide if force is false and not interacting
      this.hide();
    }
  },

  destroyed() {
    this.hide();
    if (!this.dismissOnClickAway) {
      this.trigger.removeEventListener("mouseenter", this.show);
      this.trigger.removeEventListener("mouseleave", this.hide);
      this.trigger.removeEventListener("focus", this.show);
      this.trigger.removeEventListener("blur", this.hide);
    }
    if (this._documentClickHandler) {
      document.removeEventListener("click", this._documentClickHandler);
    }
    if (this._clickAwayHandler) {
      document.removeEventListener("click", this._clickAwayHandler);
    }
    this.trigger.removeEventListener("click", this.handleTriggerClick);
    this.trigger.removeAttribute("aria-describedby");
  },
};
